import { baseApi } from './baseApi';
import { mapAssetToFolderManager, sortByDate } from '../helpers';
import { FolderManagerMimeTypes } from '../interfaces';
import { addNotification } from '../store';
import { isAbortError } from '../helpers/apiErrors';
import { captureError } from '@clatter/platform';
import { foldersService } from './folders.service';

interface RecentlyAddedApiItem {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DisplayName: string;
}

interface RecentlyAddedApiGroup {
  group_name: string | null;
  group_id: number | null;
  items: RecentlyAddedApiItem[];
}

interface ICreateAssetProps {
    parentId: number;
    data: {
      copy_of?: number;
    };
  }

export interface RecentlyAddedApiResponse {
  workspaces: RecentlyAddedApiGroup[];
  presentations: RecentlyAddedApiGroup[];
}

interface AssetItemFromApi {
  ID: number;
  Name: string;
  Type: string;
  Url: string;
  FolderID: number;
  SourceID: number;
  Source: AssetItemFromApi;
  Folder: AssetItemFromApi;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;
  Metadata: MetadataValueMap | null;
}

const getThumbnailUrl = (item: AssetItemFromApi) => {
  if (
    item.Type === FolderManagerMimeTypes.PDF ||
    item.Type === FolderManagerMimeTypes.PPTX ||
    item.Type === FolderManagerMimeTypes.ZIP
  ) {
    return null;
  }
  return item.Url;
};

export const mapApiToStore = (item: AssetItemFromApi) => ({
  id: item.ID,
  name: item.Name,
  type: item.Type,
  url: item.Url,
  thumbnailUrl: getThumbnailUrl(item),
  createdAt: item.CreatedAt,
  updatedAt: item.UpdatedAt,
  deletedAt: item.DeletedAt,
  // relations
  folderId: item.FolderID,
  sourceId: item.SourceID,
  source: item.Source,
  folder: item.Folder,
});

export const assetsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createAsset: builder.mutation<void, ICreateAssetProps>({
      query: ({ data }) => ({
        url: `/assets`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { parentId }) => [{ type: 'Folders', id: `ASSETS_${parentId}` }],
    }),
    getAssets: builder.query<IApiResponse<{ error: string } | string>, { query: string }>({
      query: ({ query }) => ({
        url: `/assets${query}`,
      }),
      transformResponse: (result: any) => (result?.data || []).map(mapAssetToFolderManager),
      providesTags: ['Assets'],
      keepUnusedDataFor: 0,
    }),
    getAssetPresignedUrl: builder.query<IApiResponse<{ error: string } | string>, { assetId: number }>({
      query: ({ assetId }) => ({
        url: `/assets/${assetId}/download`,
      }),
      keepUnusedDataFor: 0,
    }),
    getAssetVersionPresignedUrl: builder.query<
      IApiResponse<{ error: string } | string>,
      {
        assetId: number;
        versionId: number;
      }
    >({
      query: ({ assetId, versionId }) => ({
        url: `/assets/${assetId}/versions/${versionId}/download`,
      }),
      keepUnusedDataFor: 0,
    }),
    getAssetVersions: builder.query<IAssetVersion[], { assetId: number }>({
      query: ({ assetId }) => ({
        url: `/assets/${assetId}/versions`,
      }),
      transformResponse: (result: IApiResponse<IAssetVersion[]>) => sortByDate(result?.data || [], 'CreatedAt', 'desc'),
      providesTags: (result, error, { assetId }) => [
        { type: 'Assets', id: `GET_VERSIONS_URL_${assetId}` },
        {
          type: 'Assets',
          id: 'VERSIONS',
        },
      ],
    }),
    getRecentAssets: builder.query<FolderManagerItem[], void>({
      query: () => `/assets/me/recent`,
      transformResponse: (result: any) => (result.data || []).map(mapAssetToFolderManager),
      providesTags: (result, error) => [{ type: 'Assets', id: `RECENT` }, { type: 'Assets' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          if (isAbortError(error)) return;
          const message = 'Failed to fetch recent assets!';
          dispatch(
            addNotification({
              type: 'error',
              message: message,
            }),
          );
          captureError(error, { message, component: 'assets.service' });
        }
      },
    }),
    getAssetThumbnailUrl: builder.query<string, { assetId: number }>({
      query: ({ assetId }) => ({
        url: `/assets/${assetId}/thumbnail`,
      }),
      transformResponse: (result: IApiResponse<string>) => result?.data,
      providesTags: (result, error, { assetId }) => [{ type: 'Assets', id: `GET_THUMBNAIL_URL_${assetId}` }],
      keepUnusedDataFor: 0,
    }),
    getAssetById: builder.query<ReturnType<typeof mapApiToStore>, { assetId: number }>({
      query: ({ assetId }) => ({
        url: `/assets/${assetId}`,
      }),
      transformResponse: (result: any) => mapApiToStore(result.data),
      providesTags: (result, error, { assetId }) => [{ type: 'Assets', id: `GET_${assetId}` }],
    }),
    updateAsset: builder.mutation<IApiResponse<AssetItemFromApi>, IUpdateAssetProps>({
      query: ({ assetId, data }) => ({
        url: `/assets/${assetId}`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted({ assetId, data, sourceParentId }, { dispatch, queryFulfilled, getState }) {
        if (data.folder_id == null || sourceParentId == null) return;
        const targetParentId = data.folder_id;

        const sourceCache = foldersService.endpoints.getFolderAssets.select({ parentId: sourceParentId })(getState() as any);
        const movingItem = sourceCache?.data?.find((a) => a.id === assetId);

        const sourcePatch = dispatch(
          foldersService.util.updateQueryData('getFolderAssets', { parentId: sourceParentId }, (draft) => {
            const index = draft.findIndex((a) => a.id === assetId);
            if (index !== -1) draft.splice(index, 1);
          }),
        );

        const targetCache = foldersService.endpoints.getFolderAssets.select({ parentId: targetParentId })(getState() as any);
        let targetPatch: any;
        if (movingItem && targetCache?.data) {
          targetPatch = dispatch(
            foldersService.util.updateQueryData('getFolderAssets', { parentId: targetParentId }, (draft) => {
              draft.push({ ...movingItem, parentId: targetParentId });
            }),
          );
        }

        try {
          await queryFulfilled;
        } catch {
          sourcePatch.undo();
          targetPatch?.undo();
        }
      },
      invalidatesTags: () => [{ type: 'Folders' }, { type: 'Assets' }, { type: 'Presentations' }],
    }),
    deleteAssetById: builder.mutation<void, { assetId: number }>({
      query: ({ assetId }) => ({
        url: `/assets/${assetId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'Folders' }, { type: 'Presentations' }],
    }),
    getRecentlyAdded: builder.query<RecentlyAddedApiResponse, void>({
      query: () => `/me/recently-added`,
      transformResponse: (result: any) => result.data,
      providesTags: () => [{ type: 'Assets', id: 'RECENTLY_ADDED' }],
    }),
  }),
});

export const {
  useGetAssetsQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useGetRecentAssetsQuery,
  useGetAssetVersionsQuery,
  useLazyGetAssetVersionsQuery,
  useLazyGetAssetByIdQuery,
  useDeleteAssetByIdMutation,
  useGetAssetThumbnailUrlQuery,
  useLazyGetAssetPresignedUrlQuery,
  useLazyGetAssetVersionPresignedUrlQuery,
  useGetRecentlyAddedQuery,
} = assetsService;
